import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  formatCurrency,
  formatGrossFromNet,
  grossAmountFromNet,
  netFromGross,
  parseAmount,
} from "@/lib/money";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  LockedAutocomplete,
  type AutocompleteOption,
} from "@/components/LockedAutocomplete";
import { articleSource } from "@/lib/autocompleteSources";
import type { Article, Invoice } from "@/types/camping";

export interface InvoiceLineCreatePayload {
  article_id: string;
  quantity: string;
  unit_price?: string | null;
  description?: string | null;
}

export interface AddInvoiceLineModalProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onAdded?: () => void;
}

export function AddInvoiceLineModal({
  open,
  invoice,
  onClose,
  onAdded,
}: AddInvoiceLineModalProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articleOption, setArticleOption] = useState<AutocompleteOption<Article> | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedArticle = articleOption?.value ?? null;

  const loadArticleOptions = useCallback(
    (query: string) => articleSource(articles, query),
    [articles],
  );

  useEffect(() => {
    if (!open) return;
    setLoadingArticles(true);
    api
      .get<Article[]>("/api/v1/articles")
      .then((rows) => setArticles(rows.filter((a) => a.is_active)))
      .catch(() => setArticles([]))
      .finally(() => setLoadingArticles(false));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setArticleOption(null);
      setQuantity("1");
      setUnitPrice("");
      setDescription("");
      setError(null);
      return;
    }
    if (selectedArticle?.current_price != null && !unitPrice) {
      const gross = grossAmountFromNet(
        selectedArticle.current_price,
        selectedArticle.tax_rate_percent,
      );
      if (gross != null) setUnitPrice(String(gross));
    }
  }, [open, selectedArticle, unitPrice]);

  function handleArticleChange(option: AutocompleteOption<Article> | null) {
    setArticleOption(option);
    const art = option?.value;
    if (art?.current_price != null) {
      const gross = grossAmountFromNet(art.current_price, art.tax_rate_percent);
      setUnitPrice(gross != null ? String(gross) : "");
    } else {
      setUnitPrice("");
    }
    setDescription("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!invoice) return;
    const articleId = articleOption?.value.id ?? "";
    const qty = parseFloat(quantity.replace(",", "."));
    if (!articleId || !Number.isFinite(qty) || qty <= 0) {
      setError("Bitte Artikel und gültige Menge wählen.");
      return;
    }
    const payload: InvoiceLineCreatePayload = {
      article_id: articleId,
      quantity: String(qty),
    };
    if (unitPrice.trim()) {
      const grossPrice = parseAmount(unitPrice.replace(",", "."));
      if (grossPrice == null) {
        setError("Einzelpreis ist ungültig.");
        return;
      }
      const taxRate = Number(selectedArticle?.tax_rate_percent) || 0;
      const netPrice = netFromGross(grossPrice, taxRate);
      payload.unit_price = String(Math.round(netPrice * 100) / 100);
    }
    if (description.trim()) {
      payload.description = description.trim();
    }

    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/api/v1/invoices/${invoice.id}/lines`, payload);
      onAdded?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Position konnte nicht hinzugefügt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  const previewTotal = useMemo(() => {
    if (!selectedArticle || !quantity || !unitPrice) return null;
    return (
      (parseFloat(quantity.replace(",", ".")) || 0) *
      (parseFloat(unitPrice.replace(",", ".")) || 0)
    );
  }, [selectedArticle, quantity, unitPrice]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Position hinzufügen"
      description={
        invoice
          ? `Rechnung ${invoice.invoice_number} – Artikel aus Stammdaten`
          : undefined
      }
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {loadingArticles ? (
          <p className="text-sm text-label-secondary">Lade Artikel…</p>
        ) : (
          <LockedAutocomplete
            label="Artikel"
            required
            placeholder="Artikelnummer oder Bezeichnung suchen…"
            value={articleOption}
            loadOptions={loadArticleOptions}
            onChange={handleArticleChange}
          />
        )}

        <label className="flex flex-col gap-1 text-sm">
          Menge
          <input
            type="number"
            min="0.01"
            step="0.01"
            required
            className="eh-input"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          Einzelpreis (brutto)
          <input
            type="number"
            min="0"
            step="0.01"
            className="eh-input"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            placeholder={
              selectedArticle?.current_price != null
                ? formatGrossFromNet(
                    selectedArticle.current_price,
                    selectedArticle.tax_rate_percent,
                  )
                : "aus Stammdaten"
            }
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          Beschreibung (optional)
          <input
            className="eh-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={selectedArticle?.name ?? ""}
          />
        </label>

        {previewTotal != null ? (
          <p className="text-sm text-label-secondary">
            Zeilensumme (brutto):{" "}
            <span className="font-medium tabular-nums text-label">
              {formatCurrency(previewTotal)}
            </span>
          </p>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting} disabled={loadingArticles}>
            Position hinzufügen
          </Button>
        </div>
      </form>
    </Modal>
  );
}